import React, { useState } from 'react';
import { ChangeLog, User } from '../types';
import { getChangeSummary, getImpactColor } from '../services/changeTracking';
import { Clock, User as UserIcon, AlertCircle, Filter } from 'lucide-react';
import { parseDateLike } from '../utils/dateUtils';

const toTime = (value: unknown, fallback: number = 0): number => {
  const parsedDate = parseDateLike(value);
  return parsedDate ? parsedDate.getTime() : fallback;
};

interface ChangeHistoryProps {
  changes: ChangeLog[];
  users: User[];
  maxItems?: number;
}

export default function ChangeHistory({ changes, users, maxItems = 50 }: ChangeHistoryProps) {
  const [filter, setFilter] = useState<'all' | ChangeLog['changeType']>('all');
  const [showAll, setShowAll] = useState(false);

  // Filter changes
  const filteredChanges = changes.filter((change) => {
    if (filter === 'all') return true;
    return change.changeType === filter;
  });

  // Sort by date descending
  const sortedChanges = [...filteredChanges].sort(
    (a, b) => toTime(b.changedAt) - toTime(a.changedAt)
  );

  // Limit display
  const displayChanges = showAll ? sortedChanges : sortedChanges.slice(0, maxItems);

  const getUserName = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user?.name || 'Unknown User';
  };

  const formatDateTime = (dateStr: string) => {
    const date = parseDateLike(dateStr);
    if (!date) return 'Unknown time';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  if (changes.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No change history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <select
          id="change-history-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="px-3 py-1 border rounded-lg text-sm"
          title="Filter changes by type"
          aria-label="Filter changes by type"
        >
          <option value="all">All Changes ({changes.length})</option>
          <option value="status">Status Changes</option>
          <option value="targetDate">Due Date Changes</option>
          <option value="estimate">Estimate Changes</option>
          <option value="cost">Cost Changes</option>
          <option value="priority">Priority Changes</option>
          <option value="assignee">Assignee Changes</option>
          <option value="complexity">Complexity Changes</option>
          <option value="other">Other Changes</option>
        </select>
      </div>

      {/* Change Timeline */}
      <div className="space-y-3">
        {displayChanges.map((change, index) => (
          <div
            key={change.id}
            className="border-l-4 border-border pl-4 pb-3 relative"
          >
            {/* Timeline dot */}
            <div className="absolute -left-2 top-1 w-4 h-4 rounded-full bg-white bg-muted/40 border-2 border-primary/40" />

            {/* Change content */}
            <div className="bg-muted/40 rounded-lg p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    {getChangeSummary(change)}
                  </p>
                  
                  {change.reason && (
                    <div className="flex items-start gap-1 text-xs text-muted-foreground">
                      <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>Reason: {change.reason}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <UserIcon className="w-3 h-3" />
                      {change.changedByName || getUserName(change.changedBy)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDateTime(change.changedAt)}
                    </span>
                  </div>
                </div>

                {/* Impact badge */}
                {change.impactLevel && (
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${getImpactColor(
                      change.impactLevel
                    )}`}
                  >
                    {change.impactLevel}
                  </span>
                )}
              </div>

              {/* Show old/new values for important changes */}
              {(change.changeType === 'estimate' ||
                change.changeType === 'cost' ||
                change.changeType === 'targetDate') && (
                <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-border">
                  <div>
                    <span className="text-muted-foreground">Previous:</span>{' '}
                    <span className="font-medium text-destructive">
                      {change.oldValue || 'None'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Current:</span>{' '}
                    <span className="font-medium text-chart-2">
                      {change.newValue || 'None'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Show more/less */}
      {sortedChanges.length > maxItems && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-sm text-primary hover:text-primary font-medium"
        >
          {showAll
            ? 'Show Less'
            : `Show ${sortedChanges.length - maxItems} More Changes`}
        </button>
      )}
    </div>
  );
}
